/**
 * The account menu — DESIGN.md §13.1; Long-Day Review SHELL-18.
 *
 * Sign out used to be a bare icon button sitting next to the theme toggle: one
 * mis-click ended the session. It is not destructive enough to deserve a
 * confirm dialog, so it gets the other kind of protection — you have to open a
 * menu to reach it.
 *
 * The menu also carries the two settings people reach for most often on a long
 * day: text size (Brad works zoomed in, so A−/A+ needs to be one click from
 * anywhere) and theme. Both also live in Settings › Appearance, spelled out.
 */
import { Link } from '@tanstack/react-router';
import { ChevronsUpDown, LogOut, Monitor, Moon, Settings, Sun } from 'lucide-react';
import { UserAvatar, type AvatarUser } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/shell/dropdown-menu';
import { useTheme, type ThemeMode } from '@/lib/theme';
import { TEXT_SIZES, TEXT_SIZE_LABELS, useTextSize } from '@/lib/prefs';
import { cn } from '@/lib/utils';

const THEME_OPTIONS: { mode: ThemeMode; label: string; Icon: typeof Sun }[] = [
  { mode: 'dark', label: 'Dark', Icon: Moon },
  { mode: 'light', label: 'Light', Icon: Sun },
  { mode: 'system', label: 'System', Icon: Monitor },
];

export function UserMenu({
  user,
  name,
  email,
  collapsed,
  onSignOut,
  signOutPending,
  onNavigate,
}: {
  user: AvatarUser;
  name: string;
  email: string;
  collapsed: boolean;
  onSignOut: () => void;
  signOutPending: boolean;
  onNavigate?: () => void;
}): JSX.Element {
  const { mode, setMode } = useTheme();
  const [textSize, setTextSize] = useTextSize();

  const index = TEXT_SIZES.indexOf(textSize);
  const step = (delta: number): void => {
    const next = TEXT_SIZES[Math.min(TEXT_SIZES.length - 1, Math.max(0, index + delta))];
    if (next) setTextSize(next);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className={cn(
          'hover:bg-accent focus-visible:ring-ring flex w-full items-center gap-2 rounded-md p-1.5 text-left transition-colors',
          collapsed && 'justify-center',
        )}
        aria-label={`Account menu for ${name}`}
      >
        <UserAvatar user={user} />
        {collapsed ? null : (
          <>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm font-medium">{name}</span>
              <span className="text-muted-foreground block truncate text-xs">{email}</span>
            </span>
            <ChevronsUpDown className="text-muted-foreground size-4 shrink-0" aria-hidden="true" />
          </>
        )}
      </DropdownMenuTrigger>

      <DropdownMenuContent side="top" align="start">
        <DropdownMenuLabel>
          <span className="block truncate text-sm font-medium">{name}</span>
          <span className="text-muted-foreground block truncate text-xs font-normal">{email}</span>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />

        <DropdownMenuItem asChild>
          <Link to="/settings" onClick={onNavigate}>
            <Settings aria-hidden="true" />
            Settings
          </Link>
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        {/* Text size — the stepper, not the full control (that is in Settings). */}
        <div className="flex items-center gap-1 px-2 py-1.5" role="group" aria-label="Text size">
          <span className="flex-1 text-xs font-medium">
            Text size
            <span className="text-muted-foreground ml-1.5 font-normal">
              {TEXT_SIZE_LABELS[textSize]}
            </span>
          </span>
          <DropdownMenuItem
            className="justify-center px-2 py-1 font-semibold"
            aria-label="Smaller text"
            disabled={index === 0}
            onSelect={(event) => {
              // Stepping should not close the menu: people step twice.
              event.preventDefault();
              step(-1);
            }}
          >
            A<span className="text-xs">−</span>
          </DropdownMenuItem>
          <DropdownMenuItem
            className="justify-center px-2 py-1 font-semibold"
            aria-label="Larger text"
            disabled={index === TEXT_SIZES.length - 1}
            onSelect={(event) => {
              event.preventDefault();
              step(1);
            }}
          >
            A<span className="text-xs">+</span>
          </DropdownMenuItem>
        </div>

        <div className="flex items-center gap-1 px-2 py-1.5" role="group" aria-label="Theme">
          <span className="flex-1 text-xs font-medium">Theme</span>
          {THEME_OPTIONS.map(({ mode: option, label, Icon }) => (
            <DropdownMenuItem
              key={option}
              className={cn('justify-center px-2 py-1', mode === option && 'bg-accent')}
              aria-label={`Theme: ${label}`}
              aria-current={mode === option}
              onSelect={(event) => {
                event.preventDefault();
                setMode(option);
              }}
            >
              <Icon aria-hidden="true" />
            </DropdownMenuItem>
          ))}
        </div>

        <DropdownMenuSeparator />

        <DropdownMenuItem
          disabled={signOutPending}
          onSelect={() => onSignOut()}
          className="text-destructive focus:text-destructive"
        >
          <LogOut aria-hidden="true" />
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
